import { decodeBusy, encodeBusy, type BusyInterval } from "./calendar";
import { COLORS, findPlace, placeFromIana, placeId, type Place } from "./places";
import { parseDate, todayUtc, zonedLocalToUtc } from "./time";

export type Person = Place & {
  color: string;
  isYou: boolean;
  memberId?: string;
  busy?: BusyInterval[];
  displayName?: string;
  ready?: boolean;
};

export const DURATIONS = [15, 30, 45, 60] as const;
export type Duration = (typeof DURATIONS)[number];

export function parseDuration(value: number): Duration {
  return (DURATIONS as readonly number[]).includes(value) ? (value as Duration) : 60;
}

export type PlannerState = {
  people: Person[];
  date: string;
  duration: Duration;
  selectedHour: number | null;
  activeId: string | null;
  busy: BusyInterval[];
  finalized?: boolean;
};

export function encodeState(state: PlannerState): string {
  const params = new URLSearchParams();
  for (const person of state.people) {
    params.append("p", `${person.iana}|${person.city}|${person.province}`);
  }
  if (!state.people.length) params.set("n", "0");
  params.set("d", state.date);
  params.set("m", String(state.duration));
  if (state.selectedHour !== null) params.set("h", String(state.selectedHour));
  const packedBusy = state.people.flatMap((p) => p.busy ?? []);
  const busy = packedBusy.length ? packedBusy : state.busy;
  if (busy.length) params.set("b", encodeBusy(busy));
  return params.toString();
}

export function decodeState(hash: string): PlannerState | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const entries = params.getAll("p");
  if (!entries.length && params.get("n") !== "0") return null;
  const people: Person[] = [];
  for (const entry of entries) {
    const [iana, city, province] = entry.split("|");
    if (!iana) continue;
    const catalog = (city ? findPlace(iana, city, province) : undefined) ?? placeFromIana(iana);
    const place = city && catalog.city !== city ? { ...catalog, city, province: province ?? catalog.province } : catalog;
    people.push({
      ...place,
      color: COLORS[people.length % COLORS.length],
      isYou: people.length === 0,
    });
  }
  if (!people.length && params.get("n") !== "0") return null;
  const duration = parseDuration(Number(params.get("m") ?? 60));
  const hourRaw = params.get("h");
  const selectedHour =
    hourRaw === null || hourRaw === "" ? null : Math.min(23, Math.max(0, Number(hourRaw)));
  const busy = decodeBusy(params.get("b"));
  if (busy.length && people[0] && !(people[0].busy && people[0].busy.length)) {
    people[0] = { ...people[0], busy, ready: true };
  }
  return {
    people,
    date: params.get("d") || todayUtc(),
    duration,
    selectedHour: Number.isFinite(selectedHour as number) ? selectedHour : null,
    activeId: people[0] ? placeId(people[0]) : null,
    busy,
  };
}

export function meetingStartUtc(state: PlannerState): Date | null {
  if (state.selectedHour === null || !state.people[0]) return null;
  const { year, month, day } = parseDate(state.date);
  return zonedLocalToUtc(
    year,
    month,
    day,
    state.selectedHour,
    0,
    state.people[0].iana,
  );
}

function googleStamp(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
}

export function googleCalendarUrl(title: string, start: Date, durationMin: number, details: string): string {
  const end = new Date(start.getTime() + durationMin * 60_000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${googleStamp(start)}/${googleStamp(end)}`,
    details,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function outlookUrl(title: string, start: Date, durationMin: number, details: string): string {
  const end = new Date(start.getTime() + durationMin * 60_000);
  const params = new URLSearchParams({
    rru: "addevent",
    subject: title,
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    body: details,
    path: "/calendar/action/compose",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export function meetUrl(): string {
  return "https://meet.google.com/new";
}
